import * as React from 'react';
import { Building2, Search, X } from 'lucide-react';
import { Page, PageHeader } from '@ampm-7en-ai/ui/components/Page';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { ListCard, ListRow, ListRowSkeleton } from '@ampm-7en-ai/ui/components/ListRow';
import { EmptyState } from '@ampm-7en-ai/ui/components/EmptyState';
import { Input } from '@ampm-7en-ai/ui/components/Input';
import { Select } from '@ampm-7en-ai/ui/components/Select';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Pill } from '@ampm-7en-ai/ui/components/StatusBadge';
import { Avatar } from '@ampm-7en-ai/ui/components/Avatar';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { useBusinesses, type BusinessItem } from '@/hooks/useBusinesses';
import { useBusinessPagination } from '@/hooks/useBusinessPagination';
import { useBusinessSearch } from '@/hooks/useBusinessSearch';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * Every business on the platform.
 *
 * A list rather than a grid of cards: superadmins come here to find one
 * business among many, and scanning a column of names beats reading tiles.
 * The filters sit above it and the pager below, so the rows are the page.
 */

const STATUS_OPTIONS = [
  { value: 'all', label: 'Any status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Disabled' },
];

/** The fallback plans, for before the real ones arrive. */
const FALLBACK_PLANS = ['Free', 'Standard', 'Enterprise'];

const initials = (name: BusinessItem['name']) =>
  name
    ?.split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('') || '?';

/** A date said plainly, or left alone if it isn't one. */
const when = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const isActive = (status: string) => status?.toLowerCase() === 'active';

/** What a row says about a business under its name. */
function BusinessMeta({ business }: { business: BusinessItem }) {
  const counts = [
    `${business.agents} ${business.agents === 1 ? 'agent' : 'agents'}`,
    `${business.admins} ${business.admins === 1 ? 'admin' : 'admins'}`,
  ];

  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {business.domain && <span className="font-ui-mono text-ui-xs text-ui-fg-subtle">{business.domain}</span>}
      <span className="text-ui-xs text-ui-fg-subtle">{counts.join(' · ')}</span>
      {business.created && <span className="text-ui-xs text-ui-fg-subtle">Since {when(business.created)}</span>}
    </span>
  );
}

export default function BusinessListV2() {
  const [status, setStatus] = React.useState('all');
  const [plan, setPlan] = React.useState('all');
  const { pageUrl, offset, nextPage, previousPage, resetPagination } = useBusinessPagination();
  const { draftSearch, setDraftSearch, searchQuery: search, applySearch, clearSearch } = useBusinessSearch(resetPagination);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const { data, isLoading, isFetching, isError, error, isPlaceholderData, refetch } = useBusinesses({
    search, plan, status, pageUrl, retainPreviousPage: true,
  });
  const { subscriptionPlans } = useSubscription({ fetchCurrent: false, fetchAllPlans: true, fetchInvoice: false });

  const shown = data;

  const businesses = shown?.items ?? [];
  const total = shown?.count ?? businesses.length;
  const paging = isFetching && isPlaceholderData;

  const planOptions = React.useMemo(() => {
    const names = Array.from(
      new Set((subscriptionPlans ?? []).map((one) => one?.name?.trim()).filter((name): name is string => Boolean(name)))
    );
    const offered = names.length > 0 ? names : FALLBACK_PLANS;
    return [{ value: 'all', label: 'Any plan' }, ...offered.map((name) => ({ value: name, label: name }))];
  }, [subscriptionPlans]);

  // A plan that disappears from the list shouldn't keep filtering by itself.
  React.useEffect(() => {
    if (plan !== 'all' && !planOptions.some((option) => option.value === plan)) {
      setPlan('all');
      resetPagination();
    }
  }, [planOptions, plan, resetPagination]);

  /** Searching and filtering both start the list again from the first page. */
  const refine = (change: () => void) => {
    change();
    resetPagination();
  };

  const turnTo = (url: string | null, direction: 'next' | 'previous') => {
    if (!url || isFetching) return;
    if (direction === 'next') {
      nextPage(url, businesses.length);
    } else {
      previousPage(url);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filtered = search || status !== 'all' || plan !== 'all';

  return (
    <Page>
      <PageHeader
        title="Businesses"
        description="Every workspace on the platform, what they are on, and how much they have built."
        meta={!isLoading && !isError && !isPlaceholderData && total > 0 ? <Pill>{total.toLocaleString()}</Pill> : undefined}
      />

      {isError && (
        <Callout tone="danger" className="mb-4" title="Couldn’t load the businesses">
          {error instanceof Error ? error.message : 'Something went wrong fetching the list.'}
        </Callout>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="relative min-w-[14rem] flex-1"
          onSubmit={(event) => {
            event.preventDefault();
            applySearch();
          }}
        >
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ui-fg-subtle" aria-hidden />
          <Input
            ref={searchRef}
            aria-label="Search businesses"
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
            placeholder="Type business name and hit enter"
            className="pl-9 pr-10"
          />
          {draftSearch && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                clearSearch();
                searchRef.current?.focus();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-ui-sm p-1 text-ui-fg-subtle hover:text-ui-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </form>

        <Select
          aria-label="Status"
          className="w-40"
          options={STATUS_OPTIONS}
          value={status}
          onValueChange={(value) => refine(() => setStatus(value))}
        />
        <Select
          aria-label="Plan"
          className="w-44"
          options={planOptions}
          value={plan}
          onValueChange={(value) => refine(() => setPlan(value))}
        />

        {filtered && (
          <Button
            variant="ghost"
            onClick={() => {
              clearSearch();
              setStatus('all');
              setPlan('all');
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {isError && !shown ? (
        <Panel padding="md">
          <Button onClick={() => void refetch()}>Try again</Button>
        </Panel>
      ) : isLoading && !shown ? (
        <ListCard>
          {Array.from({ length: 6 }, (_, index) => (
            <li key={index}>
              <ListRowSkeleton />
            </li>
          ))}
        </ListCard>
      ) : businesses.length === 0 ? (
        <Panel variant="dashed">
          <EmptyState
            icon={Building2}
            title={filtered ? 'Nothing matches that' : 'No businesses yet'}
            description={
              filtered
                ? 'Try a different search, or clear the filters to see everything.'
                : 'Businesses appear here as soon as someone signs up.'
            }
          />
        </Panel>
      ) : (
        <ListCard className={paging ? 'opacity-50 transition-opacity' : undefined}>
          {businesses.map((business) => (
            <li key={business.id ?? business.name}>
              <ListRow
                leading={<Avatar initials={initials(business.name)} tone="accent" />}
                title={<span className="truncate">{business.name?.trim() || 'Unnamed business'}</span>}
                description={<BusinessMeta business={business} />}
                meta={
                  <span className="flex items-center gap-2">
                    {business.plan && <Pill>{business.plan}</Pill>}
                    <Pill tone={isActive(business.status) ? 'success' : 'neutral'}>
                      {isActive(business.status) ? 'Active' : 'Disabled'}
                    </Pill>
                  </span>
                }
                href={business.id ? `/v2/businesses/${business.id}` : undefined}
              />
            </li>
          ))}
        </ListCard>
      )}

      {businesses.length > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-ui-xs text-ui-fg-subtle">
            {paging ? 'Loading…' : isError ? 'Previously loaded results' : `${(offset + 1).toLocaleString()}–${(offset + businesses.length).toLocaleString()} of ${total.toLocaleString()}`}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="secondary" disabled={!shown?.previous || isFetching} onClick={() => turnTo(shown?.previous ?? null, 'previous')}>
              Previous
            </Button>
            <Button variant="secondary" disabled={!shown?.next || isFetching} onClick={() => turnTo(shown?.next ?? null, 'next')}>
              Next
            </Button>
          </div>
        </div>
      )}
    </Page>
  );
}
